import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { asList, getJson, money, periodLabel, shortDate } from '../helpers';
import { CLARET_REPORTS, reportPath } from '../reports';

const TRANSACTION_LABELS = {
  ap_invoice: 'AP Invoice',
  project_billing: 'Project Billing',
  labor_cost: 'Labor Cost',
  project_expense: 'Project Expense',
  ap_invoice_payment: 'AP Invoice Payment',
  project_billing_payment: 'Project Billing Payment',
};

function transactionLabel(type) {
  return TRANSACTION_LABELS[type] || type || 'Transaction';
}

function unpostedRecordPath(row) {
  const projectVuid = row.project_vuid;
  const recordVuid = row.transaction_vuid;
  if (!projectVuid || !recordVuid) return null;
  switch (row.transaction_type) {
    case 'ap_invoice':
      return `/procore/projects/${projectVuid}/invoicing/ap/${recordVuid}`;
    case 'project_billing':
      return `/procore/projects/${projectVuid}/invoicing/ar/${recordVuid}`;
    case 'labor_cost':
      return `/procore/projects/${projectVuid}/labor-costs?project=${projectVuid}`;
    case 'project_expense':
      return `/procore/projects/${projectVuid}/project-expenses?project=${projectVuid}`;
    default:
      return null;
  }
}

function sortPeriods(periods) {
  return [...periods].sort((a, b) => {
    if (a.status === 'open' && b.status !== 'open') return -1;
    if (a.status !== 'open' && b.status === 'open') return 1;
    if (a.year !== b.year) return b.year - a.year;
    return b.month - a.month;
  });
}

function ReportShell({ projectVuid, projectName, report, children, periods, selectedPeriod, onPeriodChange, showPeriod }) {
  return (
    <div className="pc-page">
      <div className="pc-crumb">
        {projectVuid ? (
          <>
            <Link to="/procore">Portfolio</Link>
            {' / '}
            <Link to={`/procore/projects/${projectVuid}`}>{projectName || 'Project'}</Link>
            {' / '}
            <Link to={reportPath(projectVuid)}>Reports</Link>
            {' / '}
            {report.name}
          </>
        ) : (
          <>
            <Link to={reportPath()}>Company / Reports</Link>
            {' / '}
            {report.name}
          </>
        )}
      </div>
      <div className="pc-title-row">
        <h1>{report.name}</h1>
      </div>
      {showPeriod ? (
        <div className="pc-toolbar">
          <label className="pc-toolbar-label">
            As of period
            <select value={selectedPeriod} onChange={(event) => onPeriodChange(event.target.value)}>
              {periods.length === 0 ? <option value="">No periods</option> : null}
              {periods.map((period) => (
                <option key={period.vuid} value={period.vuid}>
                  {periodLabel(period)}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}
      {children}
    </div>
  );
}

function DataTable({ columns, rows, empty, rowHref }) {
  const navigate = useNavigate();
  if (!rows.length) {
    return <div className="pc-card"><div className="pc-empty">{empty}</div></div>;
  }
  return (
    <div className="pc-card pc-report-card">
      <div className="pc-table-scroll">
        <table className="pc-table pc-report">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} className={column.right ? 'pc-right' : undefined}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const href = rowHref ? rowHref(row) : null;
              return (
                <tr
                  key={row.id || row.transaction_vuid || index}
                  className={href ? 'pc-clickable' : undefined}
                  onClick={() => href && navigate(href)}
                >
                  {columns.map((column, colIndex) => (
                    <td key={column.key} className={column.right ? 'pc-right' : undefined}>
                      {colIndex === 0 && href ? (
                        <Link to={href} onClick={(event) => event.stopPropagation()}>
                          {column.render ? column.render(row) : row[column.key]}
                        </Link>
                      ) : (
                        column.render ? column.render(row) : row[column.key]
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GeneralLedgerTable({ rows, summary, empty }) {
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    setExpanded(new Set());
  }, [rows]);

  const toggleAccount = (accountId) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  if (!rows.length) {
    return <div className="pc-card"><div className="pc-empty">{empty}</div></div>;
  }

  const balanced = summary && Math.abs(Number(summary.balance || 0)) < 0.01;

  return (
    <div className="pc-card">
      <table className="pc-table">
        <thead>
          <tr>
            <th className="pc-gl-expand" aria-label="Expand" />
            <th>Account</th>
            <th>Account Name</th>
            <th className="pc-right">Total Debits</th>
            <th className="pc-right">Total Credits</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((account) => {
            const accountId = account.account_vuid || account.id;
            const transactions = account.transactions || [];
            const isExpanded = expanded.has(accountId);
            return (
              <React.Fragment key={accountId}>
                <tr
                  className={transactions.length ? 'pc-clickable' : undefined}
                  onClick={() => transactions.length && toggleAccount(accountId)}
                >
                  <td className="pc-gl-expand">
                    {transactions.length ? (
                      <button
                        type="button"
                        className={`pc-gl-chevron${isExpanded ? ' is-open' : ''}`}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Collapse account' : 'Expand account'}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleAccount(accountId);
                        }}
                      >
                        ▸
                      </button>
                    ) : null}
                  </td>
                  <td>{account.account_number || '—'}</td>
                  <td>{account.account_name || '—'}</td>
                  <td className="pc-right">{account.total_debits ? money(account.total_debits) : ''}</td>
                  <td className="pc-right">{account.total_credits ? money(account.total_credits) : ''}</td>
                </tr>
                {isExpanded
                  ? transactions.map((transaction) => (
                      <tr key={transaction.line_item_vuid} className="pc-gl-detail">
                        <td />
                        <td colSpan={2}>
                          <div className="pc-gl-txn">
                            <strong>
                              {transactionLabel(transaction.transaction_type)}
                              {transaction.reference_number ? ` — ${transaction.reference_number}` : ''}
                            </strong>
                            <div className="pc-sub">{transaction.description || 'No description'}</div>
                            {transaction.project ? (
                              <div className="pc-sub">
                                Project: {transaction.project.project_number} — {transaction.project.project_name}
                              </div>
                            ) : null}
                            <div className="pc-sub">{shortDate(transaction.posted_at)}</div>
                            {(transaction.external_ids || []).map((extId, index) => (
                              <div key={`${extId.external_id || index}`} className="pc-sub">
                                {extId.integration_name}: {extId.external_id}
                                {extId.external_object_type ? ` (${extId.external_object_type})` : ''}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="pc-right">
                          {transaction.debit_amount > 0 ? money(transaction.debit_amount) : ''}
                        </td>
                        <td className="pc-right">
                          {transaction.credit_amount > 0 ? money(transaction.credit_amount) : ''}
                        </td>
                      </tr>
                    ))
                  : null}
              </React.Fragment>
            );
          })}
          {summary ? (
            <tr className="pc-gl-total">
              <td colSpan={3}><strong>TOTAL</strong></td>
              <td className="pc-right"><strong>{money(summary.total_debits)}</strong></td>
              <td className="pc-right"><strong>{money(summary.total_credits)}</strong></td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {summary ? (
        <div className={`pc-gl-balance${balanced ? ' is-ok' : ' is-off'}`}>
          Balance check:{' '}
          {balanced
            ? 'Balanced (debits = credits)'
            : `Out of balance by ${money(Math.abs(summary.balance || 0))}`}
        </div>
      ) : null}
    </div>
  );
}

export default function ClaretReportPage({ reportId, projectVuid }) {
  const report = CLARET_REPORTS.find((item) => item.id === reportId) || {
    id: reportId,
    name: 'Report',
    description: '',
  };
  const [projectName, setProjectName] = useState('');
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const needsPeriod = reportId !== 'commitments';

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getJson('/api/accounting-periods').catch(() => []),
      projectVuid ? getJson(`/api/projects/${projectVuid}`).catch(() => null) : Promise.resolve(null),
    ]).then(([periodData, project]) => {
      if (cancelled) return;
      const sorted = sortPeriods(asList(periodData));
      setPeriods(sorted);
      const openPeriod = sorted.find((period) => period.status === 'open');
      setSelectedPeriod((openPeriod || sorted[0] || {}).vuid || '');
      if (project?.project_name) setProjectName(project.project_name);
    }).catch((err) => {
      if (!cancelled) setError(err.response?.data?.error || 'Unable to load report setup');
    });
    return () => {
      cancelled = true;
    };
  }, [projectVuid]);

  useEffect(() => {
    if (needsPeriod && !selectedPeriod) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setSummary(null);
    const load = async () => {
      if (reportId === 'commitments') {
        const params = projectVuid ? { project_vuid: projectVuid } : {};
        return asList(await getJson('/api/commitments-report', params));
      }
      if (reportId === 'journal-entries') {
        const params = { accounting_period_vuid: selectedPeriod };
        if (projectVuid) params.project_vuid = projectVuid;
        return asList(await getJson('/api/journal-entries', params));
      }
      if (reportId === 'unposted') {
        const data = await getJson(`/api/unposted-transactions/${selectedPeriod}`);
        const list = asList(data?.unposted_transactions || data);
        return projectVuid ? list.filter((row) => row.project_vuid === projectVuid) : list;
      }
      if (reportId === 'audit') {
        const params = {};
        if (selectedPeriod) params.accounting_period_vuid = selectedPeriod;
        if (projectVuid) params.project_vuid = projectVuid;
        const data = await getJson('/api/accounting-audit-report', params);
        return asList(data?.posted_records || data);
      }
      if (reportId === 'general-ledger') {
        const data = await getJson('/api/general-ledger-report', {
          accounting_period_vuid: selectedPeriod,
        });
        if (!cancelled) setSummary(data?.summary || null);
        return asList(data?.accounts).map((account) => ({
          ...account,
          id: account.account_vuid || account.vuid,
        }));
      }
      return [];
    };
    load()
      .then((data) => {
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.error || `Unable to load ${report.name.toLowerCase()}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportId, selectedPeriod, projectVuid, needsPeriod, report.name]);

  const columns = useMemo(() => {
    if (reportId === 'commitments') {
      return [
        { key: 'commitment', label: 'Commitment', render: (row) => (
          <>
            {row.commitment_number || '—'}
            <div className="pc-sub">{row.description}</div>
            {!projectVuid && row.project ? (
              <div className="pc-sub">{row.project.project_number} — {row.project.project_name}</div>
            ) : null}
          </>
        ) },
        { key: 'vendor', label: 'Vendor', render: (row) => row.vendor?.vendor_name || row.vendor_name || '—' },
        { key: 'original_amount', label: 'Original Amount', right: true, render: (row) => money(row.original_amount) },
        { key: 'change_orders_amount', label: 'Change Orders', right: true, render: (row) => money(row.change_orders_amount) },
        { key: 'current_amount', label: 'Current Amount', right: true, render: (row) => money(row.current_amount) },
        { key: 'invoiced_amount', label: 'Invoiced', right: true, render: (row) => money(row.invoiced_amount) },
        { key: 'remaining_amount', label: 'Remaining', right: true, render: (row) => money(row.remaining_amount) },
        { key: 'status', label: 'Status' },
      ];
    }
    if (reportId === 'journal-entries') {
      return [
        { key: 'journal_number', label: '#' },
        { key: 'entry_date', label: 'Date', render: (row) => shortDate(row.entry_date || row.posted_at) },
        { key: 'description', label: 'Description' },
        { key: 'project', label: 'Project', render: (row) => row.project?.project_name || row.project_name || '—' },
        { key: 'reference_type', label: 'Type', render: (row) => row.reference_type || row.transaction_type || '—' },
        { key: 'total_amount', label: 'Amount', right: true, render: (row) => {
          const fromLines = (row.line_items || []).reduce((sum, line) => sum + Number(line.debit_amount || 0), 0);
          return money(fromLines || row.total_amount);
        } },
      ];
    }
    if (reportId === 'unposted') {
      return [
        { key: 'transaction_type', label: 'Type', render: (row) => row.transaction_type || '—' },
        { key: 'reference_number', label: 'Reference', render: (row) => row.reference_number || '—' },
        { key: 'description', label: 'Description' },
        { key: 'project_name', label: 'Project', render: (row) => row.project_name || '—' },
        { key: 'vendor_name', label: 'Vendor', render: (row) => row.vendor_name || '—' },
        { key: 'total_amount', label: 'Amount', right: true, render: (row) => money(row.total_amount) },
        { key: 'created_at', label: 'Created', render: (row) => shortDate(row.created_at) },
      ];
    }
    if (reportId === 'audit') {
      return [
        { key: 'posted_at', label: 'Posted', render: (row) => shortDate(row.posted_at) },
        { key: 'transaction_type', label: 'Type', render: (row) => row.transaction_type || '—' },
        { key: 'reference_number', label: 'Reference', render: (row) => row.reference_number || '—' },
        { key: 'project', label: 'Project', render: (row) => (
          row.project ? `${row.project.project_number} — ${row.project.project_name}` : (row.project_name || '—')
        ) },
        { key: 'period', label: 'Period', render: (row) => (
          row.accounting_period ? `${row.accounting_period.month}/${row.accounting_period.year}` : '—'
        ) },
        { key: 'total_amount', label: 'Amount', right: true, render: (row) => money(row.total_amount) },
        { key: 'sync', label: 'Sync', render: (row) => (row.is_synced ? `Synced (${row.sync_count || 1})` : 'Not synced') },
      ];
    }
    return [
      { key: 'account_number', label: 'Account' },
      { key: 'account_name', label: 'Account Name' },
      { key: 'total_debits', label: 'Total Debits', right: true, render: (row) => money(row.total_debits) },
      { key: 'total_credits', label: 'Total Credits', right: true, render: (row) => money(row.total_credits) },
    ];
  }, [reportId, projectVuid]);

  return (
    <ReportShell
      projectVuid={projectVuid}
      projectName={projectName}
      report={report}
      periods={periods}
      selectedPeriod={selectedPeriod}
      onPeriodChange={setSelectedPeriod}
      showPeriod={needsPeriod}
    >
      {loading ? (
        <div className="pc-card"><div className="pc-empty">Loading {report.name.toLowerCase()}…</div></div>
      ) : error ? (
        <div className="pc-card"><div className="pc-empty">{error}</div></div>
      ) : reportId === 'general-ledger' ? (
        <GeneralLedgerTable
          rows={rows}
          summary={summary}
          empty={`No ${report.name.toLowerCase()} data for this period.`}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          empty={`No ${report.name.toLowerCase()} data for this period.`}
          rowHref={reportId === 'unposted' ? unpostedRecordPath : undefined}
        />
      )}
    </ReportShell>
  );
}
